// Contract infrastructure — the boundary-parsing mechanism every
// shell shares.
//
// Design (decided in the API-contract chat):
//   - APP owns the DTO contract, not core (core stays dependency-free;
//     validation is a shell/app concern by nature — it handles untrusted
//     external data). App depends on zod; that is the parsing primitive,
//     not a vendored integration, so it is allowed here.
//   - Each command's `inputSchema` is a zod schema for the WIRE shape
//     (plain strings/arrays). It is the SINGLE source used both to
//     parse a request AND to emit JSON Schema for the OpenAPI doc.
//   - `parse` runs ONCE: zod validates the wire shape, then `refine`
//     lifts it into branded/refined domain types via core's smart
//     constructors. Two steps, one parse — no shell re-validates.
//   - The wire-DTO type (`z.infer`) and the domain command type stay
//     DISTINCT, bridged by `refine`, so they can evolve independently
//     (the wire contract is a published artefact with its own lifecycle).
//
// All shells (server for request-parse + OpenAPI, ui for client-side
// form validation + typed fetch, a future cli) import `appContract`
// and parse the SAME way. Change a schema here → compile errors at
// every shell.
export { contractError, defineContract, formatContractError, NOT_STORABLE, refineAnchorRef, refineAnnotationId, refineConceptId, refineContextElementRef, refineContextId, refineConversationId, refineFormatRef, refineLanguageProfileId, refineLanguageProfileRefs, refineObjectiveId, refineProjectId, refineResourceId, refineResourceRefs, refineTemplateRef, refineTermId, unstorablePath, };
import * as D from "@bilda/core";
import { Result } from "@bilda/utils";
const contractError = (path, message) => ({
    _tag: "ContractError",
    issues: [{ path, message }],
});
// zod's error → the flat issue list. Empty path (a root-level
// problem) reads as "(root)".
const toContractError = (e) => ({
    _tag: "ContractError",
    issues: e.issues.map((i) => ({
        path: i.path.map(String).join(".") || "(root)",
        message: i.message,
    })),
});
// For a shell that wants one human string (e.g. the server's
// `bad_input` envelope reason).
const formatContractError = (e) => e.issues.map((i) => `${i.path}: ${i.message}`).join("\n");
// Text the event log can hold. Postgres jsonb refuses U+0000 and an
// unpaired surrogate, so such a string would pass decide and fail at the
// append (`*_append_failed`, the Author's work seemingly lost). Only a
// crafted request or a tool input carries them: the generic refusal (QA-26).
const LONE_SURROGATE = /[\ud800-\udbff](?![\udc00-\udfff])|(?<![\ud800-\udbff])[\udc00-\udfff]/;
const storable = (s) => !s.includes("\u0000") && !LONE_SURROGATE.test(s);
const NOT_STORABLE = "holds a NUL or an unpaired surrogate";
const pathOf = (n) => {
    const keys = [];
    for (let at = n; at !== undefined; at = at.parent)
        keys.push(at.key);
    return keys.reverse().join(".") || "(root)";
};
const unstorablePath = (body) => {
    const stack = [
        { value: body },
    ];
    for (let next = stack.pop(); next !== undefined; next = stack.pop()) {
        const { value, at } = next;
        if (typeof value === "string" && !storable(value))
            return pathOf(at);
        if (typeof value !== "object" || value === null)
            continue;
        for (const [key, child] of Object.entries(value)) {
            if (!storable(key))
                return pathOf(at);
            stack.push({ value: child, at: { key, parent: at } });
        }
    }
    return undefined;
};
// defineContract — tie an `inputSchema` to a `refine` so `parse` runs
// the two-step pipeline (validate wire, then lift to domain) as one
// call, after refusing text the event log cannot hold. The returned entry's `Cmd` is inferred from `refine`.
const defineContract = (spec) => ({
    effect: spec.effect,
    summary: spec.summary,
    description: spec.description,
    inputSchema: spec.inputSchema,
    // Keep the key absent (not `undefined`) for command entries, so the
    // fold's `outputSchema !== undefined` check reads a clean presence.
    ...(spec.outputSchema !== undefined
        ? { outputSchema: spec.outputSchema }
        : {}),
    // Unstorable text is refused first, wherever it sits in the body.
    parse: (body) => {
        const unstorable = unstorablePath(body);
        if (unstorable !== undefined) {
            return Result.err(contractError(unstorable, NOT_STORABLE));
        }
        const r = spec.inputSchema.safeParse(body);
        return r.success
            ? spec.refine(r.data)
            : Result.err(toContractError(r.error));
    },
});
// === Brand-refine helpers ===
//
// Each wraps a core smart constructor, mapping its domain error into a
// field-anchored ContractError. The wire schema guarantees the input
// is a string; these decide whether it is a VALID branded value.
const refineResourceId = (path) => (s) => Result.mapErr(() => contractError(path, "not a valid resource id"))(D.Resource.Id.to(s));
const refineAnnotationId = (path) => (s) => Result.mapErr(() => contractError(path, "not a valid annotation id"))(D.Annotation.Id.to(s));
const refineConversationId = (path) => (s) => Result.mapErr(() => contractError(path, "not a valid conversation id"))(D.Conversation.Id.to(s));
const refineProjectId = (path) => (s) => Result.mapErr(() => contractError(path, "not a valid project id"))(D.Project.Id.to(s));
// Learning-BC vocabulary ids, each wrapping its core smart constructor. The
// single-id forms (required in a create command); optional filter ids wrap
// these inline (`s === undefined ? undefined : refineX`).
const refineConceptId = (path) => (s) => Result.mapErr(() => contractError(path, "not a valid concept id"))(D.Learning.Concept.Id.to(s));
const refineObjectiveId = (path) => (s) => Result.mapErr(() => contractError(path, "not a valid objective id"))(D.Learning.Objective.Id.to(s));
const refineTermId = (path) => (s) => Result.mapErr(() => contractError(path, "not a valid term id"))(D.Learning.Term.Id.to(s));
const refineContextId = (path) => (s) => Result.mapErr(() => contractError(path, "not a valid learning context id"))(D.Learning.Context.Id.to(s));
const refineLanguageProfileId = (path) => (s) => Result.mapErr(() => contractError(path, "not a valid language profile id"))(D.Language.Profile.Id.to(s));
// A list of language-profile ids → a Set of v1 Refs, the shape the
// LearningContext create workflow binds as its instruction / learner
// language facets. Mirrors `refineLearningContextRefs` (v1-pinned; the
// binding resolves by id) but over `Language.Profile`. Emptiness is NOT
// rejected here — the domain enforces the non-empty invariant and emits
// its own `empty_*_languages` refusal, so the parse layer stays purely
// syntactic. A malformed id is a client bug (field error).
const refineLanguageProfileRefs = (path) => (ids) => Result.mapErr((errs) => errs[0] ?? contractError(path, "invalid language profile"))(Result.map((refs) => new Set(refs))(Result.combineA(ids.map((s) => Result.mapErr(() => contractError(path, `not a valid language profile id: ${s}`))(Result.map((id) => D.Language.Profile.Ref.mk(id, D.Versioning.Version.v1))(D.Language.Profile.Id.to(s)))))));
// A list of resource ids → a Set of v1 whole-resource Refs — the
// shape `sendTurn` carries for the Author's @-mentioned resources
// (chat "slice 3"). Mirrors `refineLanguageProfileRefs`: v1-pinned
// (the composer doesn't track resource version, and MVP resources
// don't meaningfully version beyond v1 — same shortcut every other
// `Resource.Ref.mkWhole` call site in the app layer takes). A
// malformed id is a field error; a dangling id (mentions a resource
// that no longer exists) is NOT rejected here — same permissive-
// parse-then-best-effort-resolve split `buildLearningContextPrompt`
// already uses for grounding refs.
const refineResourceRefs = (path) => (ids) => Result.mapErr((errs) => errs[0] ?? contractError(path, "invalid resource ref"))(Result.map((refs) => new Set(refs))(Result.combineA(ids.map((s) => Result.mapErr(() => contractError(path, `not a valid resource id: ${s}`))(Result.map((id) => D.Resource.Ref.mkWhole(id, D.Versioning.Version.v1))(D.Resource.Id.to(s)))))));
// Resolve a template id against the built-in Shape registry — the SAME
// set `GET /templates` serves, so what the picker offered is what
// parses. An unknown id is a field error, not a crash. Version is
// pinned to v1: built-in shapes are static at MVP (no shape versioning
// yet — the Template BC proper revisits this).
const refineTemplateRef = (path) => (s) => D.Template.lookupShape(s) === undefined
    ? Result.err(contractError(path, `unknown template: ${s}`))
    : Result.ok(D.Template.Ref.mk(D.Template.Id.mk(s), D.Versioning.Version.v1));
// Two-step: parse the format id, then resolve it against the closed
// builtin registry. An unknown id is a client error, not a crash.
const refineFormatRef = (path) => (s) => {
    const rId = D.Format.Id.to(s);
    if (Result.isErr(rId)) {
        return Result.err(contractError(path, "not a valid format id"));
    }
    const ref = D.Format.registry.get(rId.value);
    return ref === undefined
        ? Result.err(contractError(path, `unknown format: ${s}`))
        : Result.ok(ref);
};
// A tab's anchor entity, on the wire, as a small closed `{ kind; id }`
// union — a deliberate MVP narrowing of the Context BC's full `anchor:
// EntityRef` (any of the ten `EntityRef` variants). Only the two anchor
// kinds a real tab surface can be today (a Resource, or the singleton
// Conversation) are wired at the contract boundary; widening to more
// `EntityRef` kinds as more tab surfaces exist is additive here, not a
// reshape of the domain command (which already accepts any `EntityRef`).
const refineAnchorRef = (path) => (wire) => {
    switch (wire.kind) {
        case "resource":
            return Result.map((id) => D.Resource.Ref.mkWhole(id, D.Versioning.Version.v1))(refineResourceId(path)(wire.id));
        case "conversation":
            return Result.map((id) => D.Conversation.Ref.mk(id, D.Versioning.Version.v1))(refineConversationId(path)(wire.id));
        default:
            return Result.err(contractError(path, `unsupported anchor kind: ${wire.kind}`));
    }
};
// A Context "element" on the wire — a resource id, resolved to a v1
// whole-resource ref. `Context.State.ContextElement` is a closed sum
// over resource kinds for this step (core's own documented MVP
// narrowing of the design's full `EntityRef` context item); this
// mirrors that narrowing at the parse boundary.
const refineContextElementRef = (path) => (s) => Result.map((id) => D.Resource.Ref.mkWhole(id, D.Versioning.Version.v1))(refineResourceId(path)(s));
//# sourceMappingURL=shared.js.map